import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import * as moment from 'moment';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/throw';
import 'rxjs/add/observable/timer';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/exhaustMap';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/take';
import 'rxjs/add/operator/timeout';
import {Report} from '../models/report.model';
import {GroupPayrollReportRequest, GroupReportJobHandle, HourlyComparisonRow,
  PreparedGroupReportResponse} from './report.service.types';
import {HTTP_RESPONSE_OBSERVE, REPORT_PATH, REPORT_PROGRESS_HEADER} from './user-report.constants';

const JOB_PATH = '/report-jobs';
const RESULT_PATH = 'report-jobs/';
const RESULT_SUFFIX = '/result';
const GENERATION_PARAMETER = 'generation';
const DATE_FORMAT = 'YYYY-MM-DD';
const MONTH_UNIT = 'month';
const FIRST_DAY = 1;
const FIRST_POLL_MILLIS = 0;
const POLL_MILLIS = 1500;
const MAX_WAIT_MILLIS = 7500000;
const READY_STATUS = 200;
const NOT_FOUND_STATUS = 404;
const UNSUPPORTED_STATUS = 405;
const COMPARISON_SUFFIX = '/hourly-comparison';

export function requestGroupReport(http: HttpClient, base: string,
  request: GroupPayrollReportRequest): Observable<HttpResponse<Report[]>> {
  if (!usesPreparation(request)) {
    return legacyReport(http, base, request);
  }
  return http.post<GroupReportJobHandle>(base + request.groupId + JOB_PATH, {
    from: request.from, to: request.to, progressId: request.progressId
  }).catch((error: HttpErrorResponse) => unsupported(error)
    ? Observable.of(null) : Observable.throw(error))
    .switchMap((job: GroupReportJobHandle) => job
      ? preparedReport(http, base, job) : legacyReport(http, base, request));
}

function usesPreparation(request: GroupPayrollReportRequest): boolean {
  const start = moment(request.from, DATE_FORMAT, true);
  return start.isValid() && start.date() === FIRST_DAY
    && !start.isAfter(moment(), MONTH_UNIT)
    && start.endOf(MONTH_UNIT).format(DATE_FORMAT) === request.to;
}

function preparedReport(http: HttpClient, base: string,
  job: GroupReportJobHandle): Observable<PreparedGroupReportResponse> {
  const url = base + RESULT_PATH + encodeURIComponent(job.id) + RESULT_SUFFIX;
  const params = new HttpParams().set(GENERATION_PARAMETER, job.generation);
  return Observable.timer(FIRST_POLL_MILLIS, POLL_MILLIS)
    .exhaustMap(() => http.get<Report[]>(url, {params: params, observe: HTTP_RESPONSE_OBSERVE}))
    .filter(response => response.status === READY_STATUS)
    .take(FIRST_DAY).timeout(MAX_WAIT_MILLIS)
    .map(response => Object.assign(response, {preparation: job}));
}

export function requestHourlyComparison(http: HttpClient, base: string,
  parent: GroupReportJobHandle): Observable<HourlyComparisonRow[]> {
  const url = base + RESULT_PATH + encodeURIComponent(parent.id) + COMPARISON_SUFFIX;
  return http.post<GroupReportJobHandle>(url, {generation: parent.generation})
    .switchMap(job => pollHourlyComparison(http, base, job));
}

function pollHourlyComparison(http: HttpClient, base: string,
  job: GroupReportJobHandle): Observable<HourlyComparisonRow[]> {
  const url = base + RESULT_PATH + encodeURIComponent(job.id) + COMPARISON_SUFFIX;
  const params = new HttpParams().set(GENERATION_PARAMETER, job.generation);
  return Observable.timer(FIRST_POLL_MILLIS, POLL_MILLIS)
    .exhaustMap(() => http.get<HourlyComparisonRow[]>(url, {params: params, observe: HTTP_RESPONSE_OBSERVE}))
    .filter(response => response.status === READY_STATUS)
    .take(FIRST_DAY).timeout(MAX_WAIT_MILLIS).map(response => response.body);
}

function legacyReport(http: HttpClient, base: string,
  request: GroupPayrollReportRequest): Observable<HttpResponse<Report[]>> {
  let headers = new HttpHeaders({from: request.from, to: request.to});
  if (request.progressId) {
    headers = headers.set(REPORT_PROGRESS_HEADER, request.progressId);
  }
  return http.get<Report[]>(base + request.groupId + REPORT_PATH,
    {headers: headers, observe: HTTP_RESPONSE_OBSERVE});
}

function unsupported(error: HttpErrorResponse): boolean {
  return error.status === NOT_FOUND_STATUS || error.status === UNSUPPORTED_STATUS;
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/services/group-report-job.transport.ts